import React, { useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { differenceInCalendarDays, format } from 'date-fns';
import { TaskActions } from '../actions/useTaskActions';
import { nextSession } from '../schedule';
import { TaskItem } from '../TaskItem';
import { clientHref } from '../tasks/links';
import { ClientTask, ClientWork, DashboardClient } from '../tasks/types';
import { INK, panelSx } from '../ui';

/** One fact: a label, the value, and an optional muted second line, so the three facts line up row for row. */
const Fact: React.FC<{ label: string; sub?: React.ReactNode; children: React.ReactNode }> = ({ label, sub, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography component="dt" sx={{ fontSize: 12, color: INK.muted }}>
      {label}
    </Typography>
    <Typography component="dd" sx={{ m: 0, mt: 0.25, fontWeight: 600, fontSize: 14 }}>
      {children}
      {sub ? (
        <Box component="span" sx={{ display: 'block', mt: 0.25, fontSize: 13, fontWeight: 400, color: INK.muted }}>
          {sub}
        </Box>
      ) : null}
    </Typography>
  </Box>
);

/** "Ignition expires" / "Mon 7 Dec" / "in 39 days", or "ended" / "… " / "3 days ago" once past. */
function programmeEnd(client: DashboardClient, now: Date) {
  if (!client.programme || !client.endsAt) return null;
  const days = differenceInCalendarDays(client.endsAt, now);
  const relative = days === 0 ? 'today' : days > 0 ? `in ${days} day${days === 1 ? '' : 's'}` : `${-days} day${days === -1 ? '' : 's'} ago`;
  return { label: `${client.programmeLabel} ${days < 0 ? 'ended' : 'expires'}`, date: format(client.endsAt, 'EEE d MMM'), relative };
}

/** Everything outstanding for one client, with the actions to clear it, without leaving the dashboard. */
/** `otherTasks`: the client's tasks from the other Inbox segment, listed after this one's. */
/** `showCoach`: name the lead coach; a coach's own view leaves it out, since it is always them. */
export const ClientDetailPanel: React.FC<{ item: ClientWork; otherTasks?: ClientTask[]; actions: TaskActions; now: Date; showCoach?: boolean }> = ({
  item,
  otherTasks = [],
  actions,
  now,
  showCoach = true,
}) => {
  const { client, tasks } = item;
  const heading = useRef<HTMLHeadingElement>(null);
  const renderTasks = (list: ClientTask[]) => (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {list.map((task) => (
        <TaskItem
          key={task.id}
          client={client}
          task={task}
          actions={actions}
          now={now}
          onCleared={() => requestAnimationFrame(() => heading.current?.focus())}
        />
      ))}
    </Box>
  );
  const pt = client.ptUsed !== null && client.ptTotal ? { used: client.ptUsed, total: client.ptTotal } : null;
  const ends = programmeEnd(client, now);
  const next = nextSession(client, now);
  const meta = [client.programmeLabel, client.week ? `week ${client.week}` : null, client.location, showCoach ? `Coach ${client.coachName}` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box component="section" aria-label={`${client.name}, outstanding work`} sx={{ ...panelSx, px: 2.5, pt: 1.5, pb: 1 }}>
      {/* The whole header is the way to the full profile. */}
      <Box
        component={RouterLink}
        to={clientHref(client)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mx: -1.25,
          mb: 1,
          px: 1.25,
          py: 1,
          borderRadius: '8px',
          color: 'inherit',
          textDecoration: 'none',
          transition: 'background-color 120ms ease-out',
          '&:hover': { bgcolor: '#F3F6FD' },
          '&:hover .profile-name, &:focus-visible .profile-name': { color: 'primary.dark', textDecoration: 'underline', textUnderlineOffset: '3px' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
        }}
      >
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          {/* Focus lands here after clearing removes the task the user was on. */}
          <Typography ref={heading} tabIndex={-1} component="h2" className="profile-name" sx={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em', outline: 'none' }}>
            {client.name}
          </Typography>
          <Typography sx={{ color: INK.muted, fontSize: 14, mt: 0.25 }}>{meta}</Typography>
        </Box>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, color: 'primary.main', fontSize: 13, fontWeight: 600 }}>
          Full profile
          <ChevronRight aria-hidden sx={{ fontSize: 18 }} />
        </Box>
      </Box>

      <Box
        component="dl"
        sx={{ m: 0, mb: 2.5, py: 1.5, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5, borderTop: `1px solid ${INK.faint}`, borderBottom: `1px solid ${INK.faint}` }}
      >
        {pt ? (
          <Fact label="PT used">
            {pt.used} of {pt.total}
            <Box aria-hidden sx={{ mt: 1, height: 4, borderRadius: 2, bgcolor: '#E8ECF1' }}>
              <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'primary.main', width: `${Math.min(100, Math.round((100 * pt.used) / pt.total))}%` }} />
            </Box>
          </Fact>
        ) : null}
        <Fact label="Next session" sub={next?.time}>
          {next ? next.day : 'None booked'}
        </Fact>
        {ends ? (
          <Fact label={ends.label} sub={ends.relative}>
            {ends.date}
          </Fact>
        ) : null}
      </Box>

      <Typography component="h3" sx={{ fontSize: 13, fontWeight: 600, color: INK.muted }}>
        {tasks.length === 1 ? '1 thing outstanding' : `${tasks.length} things outstanding`}
      </Typography>
      {renderTasks(tasks)}
      {otherTasks.length > 0 ? (
        <Box sx={{ mt: 1, pt: 1.5, borderTop: `1px solid ${INK.faint}` }}>
          <Typography component="h3" sx={{ fontSize: 13, fontWeight: 600, color: INK.muted }}>
            Also outstanding
          </Typography>
          {renderTasks(otherTasks)}
        </Box>
      ) : null}
    </Box>
  );
};
